import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import {
  Button,
  ComboBox,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Tag,
  TagGroup,
  TagList,
  Text,
} from "react-aria-components";
import { XIcon } from "@/components/Icon";
import styles from "./TraitCombobox.module.css";

export type TraitChoice = {
  id: number;
  code: string;
  label: string | null;
  isArchived: boolean;
};

type TraitComboboxProps = {
  name: string;
  legend: string;
  note: string;
  placeholder: string;
  traits: TraitChoice[];
  selectedIds: number[];
  conflictIds: readonly number[];
  onChange: (ids: number[]) => void;
  // Rendered only once a search has come back empty, and handed the query that
  // found nothing. Minting a trait is an ADMIN-shaped decision, so it is
  // reachable from having looked rather than from the page loading.
  renderMint?: (query: string) => ReactNode;
};

type TraitChip = TraitChoice & { hasConflict: boolean };

const traitName = (choice: TraitChoice): string => choice.label ?? choice.code;

// A search-and-chips picker rather than a checkbox per trait: the rendered
// control then scales with how many traits this template binds — a handful —
// instead of with how many traits exist. The selected set is the primary
// reading, which is the question anyone opening this page actually has.
export const TraitCombobox = ({
  name,
  legend,
  note,
  placeholder,
  traits,
  selectedIds,
  conflictIds,
  onChange,
  renderMint,
}: TraitComboboxProps) => {
  const [query, setQuery] = useState("");

  const byId = useMemo(
    () => new Map(traits.map((choice) => [choice.id, choice])),
    [traits],
  );

  // Chips resolve against every trait, options only against live ones: an
  // archived trait already bound has to stay visible to be removable, but it
  // is not something anyone may newly reach for.
  //
  // Each chip is a fresh object carrying its own conflict flag. The collection
  // caches a rendered item against the object it came from, so a chip built
  // from the unchanged trait keeps whatever it was first drawn with — and the
  // conflict is a property of the other set, which the trait cannot know.
  const chips = selectedIds
    .map((id) => byId.get(id))
    .filter((choice) => choice !== undefined)
    .map((choice) => ({
      ...choice,
      hasConflict: conflictIds.includes(choice.id),
    }));

  // A trait is found by its code as often as by its label — the code is the
  // name people carry between screens — so filtering runs here rather than
  // through the built-in one, which only ever sees a single text value.
  const options = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return traits.filter(
      (choice) =>
        !choice.isArchived &&
        (needle === "" ||
          choice.code.toLowerCase().includes(needle) ||
          (choice.label ?? "").toLowerCase().includes(needle)),
    );
  }, [traits, query]);

  return (
    <div className={styles.group}>
      <ComboBox
        className={styles.combobox}
        selectionMode="multiple"
        items={options}
        // The list arrives pre-filtered, so the built-in contains filter would
        // only narrow it a second time against the wrong text.
        defaultFilter={() => true}
        allowsEmptyCollection
        menuTrigger="focus"
        inputValue={query}
        onInputChange={setQuery}
        value={selectedIds}
        // The query is spent once it has found something; leaving it behind
        // means the next search starts inside the last one's results.
        onChange={(keys) => {
          onChange(keys.map(Number));
          setQuery("");
        }}
      >
        <Label className={styles.legend}>{legend}</Label>
        <Text slot="description" className={styles.note}>
          {note}
        </Text>
        <Input className={styles.input} placeholder={placeholder} />
        <Popover className={styles.popover}>
          <ListBox
            className={styles.listbox}
            renderEmptyState={() => (
              <p className={styles.empty}>No trait matches.</p>
            )}
          >
            {(choice: TraitChoice) => (
              <ListBoxItem
                id={choice.id}
                textValue={traitName(choice)}
                className={styles.option}
              >
                <span>{traitName(choice)}</span>
                <code className={styles.code}>{choice.code}</code>
              </ListBoxItem>
            )}
          </ListBox>
        </Popover>
      </ComboBox>

      {renderMint === undefined ||
      query.trim() === "" ||
      options.length > 0 ? null : (
        <div className={styles.mint}>{renderMint(query.trim())}</div>
      )}

      {/* Written here rather than handed to ComboBox as `name`: an archived
          trait is not in the collection, and only the ids held here are
          certain to reach the server, which is what lets it report them. */}
      {selectedIds.map((id) => (
        <input key={id} type="hidden" name={name} value={String(id)} />
      ))}

      <TagGroup
        aria-label={`${legend} traits`}
        onRemove={(keys) => onChange(selectedIds.filter((id) => !keys.has(id)))}
      >
        <TagList
          items={chips}
          className={styles.chips}
          renderEmptyState={() => <p className={styles.empty}>None.</p>}
        >
          {(choice: TraitChip) => (
            <Tag
              id={choice.id}
              textValue={traitName(choice)}
              className={
                choice.isArchived || choice.hasConflict
                  ? `${styles.chip} ${styles.chipBroken}`
                  : styles.chip
              }
            >
              <span>{traitName(choice)}</span>
              {!choice.isArchived ? null : (
                <span className={styles.chipTag}>archived</span>
              )}
              <Button
                slot="remove"
                className={styles.chipRemove}
                aria-label={`Remove ${traitName(choice)}`}
              >
                <XIcon size={13} />
              </Button>
            </Tag>
          )}
        </TagList>
      </TagGroup>
    </div>
  );
};
